import { format, addDays, isBefore, startOfDay, startOfWeek } from 'date-fns';
import { Appointment } from '@/types';
import { AppointmentFormValues } from '@/components/appointments/AppointmentForm';
import { getCurrentUser } from '@/lib/api/auth';
import { formatInTimezone } from '@/utils/timezone-utils';
import { getIanaTimeZone } from './appointment-timezone';

export const isPastScheduleDay = (day: Date): boolean =>
  isBefore(startOfDay(day), startOfDay(new Date()));

export const isScheduleTimeSlotInPast = (
  slot: string,
  date: Date,
  timezone?: string,
): boolean => {
  const tz = timezone?.trim() || getIanaTimeZone();
  const dateKey = formatInTimezone(date, tz, 'yyyy-MM-dd');
  const todayKey = formatInTimezone(new Date(), tz, 'yyyy-MM-dd');
  if (dateKey !== todayKey) return false;

  const [hours, minutes] = slot.split(':').map((part) => Number(part));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;

  const nowTime = formatInTimezone(new Date(), tz, 'HH:mm');
  const [nowHours, nowMinutes] = nowTime.split(':').map(Number);
  return hours * 60 + minutes <= nowHours * 60 + nowMinutes;
};

export const getNutritionistHostDisplay = (): { name: string; email: string } => {
  const user = getCurrentUser();
  const userRecord = user as { firstName?: string; lastName?: string } | null;
  const name =
    user?.fullName?.trim() ||
    `${userRecord?.firstName || ''} ${userRecord?.lastName || ''}`.trim() ||
    'Nutritionist';

  return {
    name,
    email: user?.email?.trim() || '',
  };
};

export const getAppointmentFormDefaultValues = (
  selectedAppointment: Appointment | null,
  selectedDate: Date | undefined,
  selectedTime: string | undefined
): Partial<AppointmentFormValues> => {
  if (selectedAppointment) {
    // Editing existing appointment - use title from API response or type as fallback
    const appointmentData = selectedAppointment as any;
    
    // Calculate end time from start time + duration
    const startTime = selectedAppointment.date;
    const endTime = new Date(startTime.getTime() + selectedAppointment.duration * 60000);
    const endTimeString = format(endTime, 'HH:mm');

    return {
      title: appointmentData.title || selectedAppointment.type || '',
      clientId: selectedAppointment.clientId.toString(),
      date: selectedAppointment.date,
      time: format(selectedAppointment.date, 'HH:mm'),
      endTime: endTimeString,
      timezone: selectedAppointment.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      description: selectedAppointment.notes || appointmentData.description || '',
      createMeetLink: (appointmentData.meetingLink || appointmentData.meeting_link) ? true : (selectedAppointment.mode === 'online' || selectedAppointment.mode === 'Online'),
      additionalAttendees: Array.isArray(appointmentData.additionalAttendees)
        ? appointmentData.additionalAttendees.join(', ')
        : Array.isArray(appointmentData.additional_attendees)
          ? appointmentData.additional_attendees.join(', ')
          : typeof appointmentData.additionalAttendees === 'string'
            ? appointmentData.additionalAttendees
            : typeof appointmentData.additional_attendees === 'string'
              ? appointmentData.additional_attendees
              : '',
      recurrence: {
        frequency: appointmentData.recurrence?.frequency || 'none',
        weeklyDays: appointmentData.recurrence?.weeklyDays || [],
        monthlyDates: appointmentData.recurrence?.monthlyDates
          || (appointmentData.recurrence?.monthlyDate ? [appointmentData.recurrence.monthlyDate] : []),
        endDate: appointmentData.recurrence?.endDate ? new Date(appointmentData.recurrence.endDate) : undefined,
      },
      updateScope: 'this_only' as const,
      deleteScope: 'this_only' as const,
    };
  }
  // Creating new appointment - use selectedDate and selectedTime if available
  const defaultStartTime = selectedTime || (() => {
    const now = new Date();
    now.setHours(now.getHours() + 1, 0, 0, 0);
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  })();
  
  // Calculate default end time (1 hour after start)
  const [startHours, startMinutes] = defaultStartTime.split(':').map(Number);
  const defaultEndTime = new Date();
  defaultEndTime.setHours(startHours + 1, startMinutes, 0, 0);
  const endTimeString = `${String(defaultEndTime.getHours()).padStart(2, '0')}:${String(defaultEndTime.getMinutes()).padStart(2, '0')}`;
  
  return {
    date: selectedDate || new Date(),
    time: defaultStartTime,
    endTime: endTimeString,
    recurrence: {
      frequency: 'none',
      weeklyDays: [],
      monthlyDates: [],
      endDate: undefined,
    },
  };
};

export const getFormattedDateRange = (selectedDate: Date | undefined, view: 'month' | 'week' | 'day') => {
  if (!selectedDate) return '';
  if (view === 'week') {
    const weekStart = startOfWeek(selectedDate);
    const weekEnd = addDays(weekStart, 6);
    return `${format(weekStart, "MMMM d")} - ${format(weekEnd, "MMMM d, yyyy")}`;
  } else if (view === 'day') {
    return format(selectedDate, 'EEEE, MMMM d, yyyy');
  } else {
    // month view
    return format(selectedDate, 'MMMM yyyy');
  }
};

